import * as React from "react";
import {cx}       from "emotion";

import "./shipping-address-form.scss";

import {cs}             from "@Common/react/chain-services";
import {consumeContext} from "@Common/react/context";
import {Types}          from "@Common/types";
import {Select}         from "@Common/ui-components/heavy/common/design2/select/select";
import {US_STATES}      from "@Common/ui-components/heavy/common/us-states";
import {CA_PROVINCES}   from "@Common/ui-components/heavy/common/canada-provinces";
import {setNestedValue} from "@Common/utils/arr-path";
import {
    entries, equalDeep,
    filterKeys
} from "@Common/utils/objects";

import {FloatingInput}            from "@Xpresso/routes/account/common/floating-labels/floating-input";
import {CheckboxLine}             from "@Xpresso/routes/common/components/checkbox-line/checkbox-line";
import {ShippingAddressFormState} from "@Xpresso/routes/common/shipping-address-form/shipping-address-form-state";

export const ShippingAddressForm = ({
    initValue = undefined,
    hasUnsavedChangesStatic : {set: setHasUnsavedChanges},
    next
}:{
    initValue ?: Types.Xpresso.Common.Shipping.AddressForm.ModelInitial,
    hasUnsavedChangesStatic : Types.Common.React.Static2.Static2Next<boolean>,
    next       : (service:Types.Xpresso.Common.Shipping.AddressForm.FormNext) => any,
}) => cs(
    ["form", ({}, next) => ShippingAddressFormState({initValue: initValue ?? {countryCode: "US"}, next})],
    consumeContext("countryCodes"),
    ({form, countryCodes}) => next({
        form,
        render: ({showErrors}) => {
            const renderField = ({path, render}) => {
                const field = form.field(path);
                const error = showErrors && field.getError();
                return (
                    <div className={cx("form-group", path, {"has-error": !!error})}>
                        {render({field, error})}
                    </div>
                );
            };

            const renderInputField = ({path, label, maxLength, placeholder=undefined, required=false, xdQaName=undefined}) => (
                renderField({
                    path,
                    render: ({field, error}) => (<>
                        <FloatingInput
                            classContainer="input-card"
                            classLabel="label"
                            textLabel={label}
                            props={{
                                className: "value",
                                maxLength: maxLength,
                                "xd-qa-type": "text_field",
                                "xd-qa-name": xdQaName,
                                defaultValue: form.value[path] || "",
                                ...{placeholder},
                                onChange: (e) => {
                                    // TODO From Quan: Don't use static this way, it's very unstable/unsafe. We can always return out the "unsavedChanges" value
                                    setHasUnsavedChanges(!equalDeep(filterKeys(initValue ?? {}, (key) => key in form.value), setNestedValue(form.value, [path], e.target.value)))
                                    field.onChange(e.target.value);
                                },
                            }}
                            required={required}
                        />
                        {error && (
                            <div className="error-message" xd-qa-type="label" xd-qa-name={xdQaName + " Error " + error.getMessage()}>
                                {error.getMessage()}
                            </div>
                        )}
                    </>),
                })
            );
            

            return (
                <div className="address-form-878">

                    <CheckboxLine
                        className="business"
                        label="Is this a business address?"
                        xdQaName="Is this a business address"
                        {...{
                            value: form.field("type").value === "office",
                            onChange: (business) => {
                                setHasUnsavedChanges(!equalDeep(filterKeys(initValue ?? {}, (key) => key in form.value), setNestedValue(form.value, ['type'], business ? "office" : "residential")))
                                form.field("type").onChange(business ? "office" : "residential")
                            }
                            ,
                        }}
                    />

                    {renderInputField({
                        path: "contactName",
                        label: "Name",
                        maxLength: 40,
                        required: true,
                        xdQaName: "Name"
                    })}

                    {renderInputField({
                        path: "company",
                        label: "Company",
                        maxLength: 80,
                        required: form.field("type").value === "office",
                        xdQaName: "Company"
                    })}
                    {renderField({
                        path: "countryCode",
                        render: ({field, error}) => <>
                            <div className="container-select">
                                {Select({
                                    list: Object.keys(countryCodes||{}),
                                    itemToLabel: (code) => countryCodes[code],
                                    isSelected: (code) => field.value === code,
                                    
                                    onChange: (countryCode) => {
                                        setHasUnsavedChanges(!equalDeep(filterKeys(initValue ?? {}, (key) => key in form.value), setNestedValue(form.value, ['countryCode'], countryCode)))
                                        field.onChange(countryCode);
                                        form.field("state").onChange(null);
                                    },
                                    autofill: {
                                        getItem: (label) => getCountryCodeFromLabel(label, countryCodes),
                                        name: "country",
                                    },
                                    xdQaName: "Country Code",
                                    className: field.value ? "valid" : null
                                })}
                                <label className="label" >Country</label>                
                                <div className="required">*</div>
                            </div>

                            {error && (
                                <div className="error-message" xd-qa-type="label" xd-qa-name={"Country Code" + " Error " + error.getMessage()}>
                                    {error.getMessage()}
                                </div>
                            )}
                        </>
                    })}                       
                    {renderInputField({
                        path: "address1",
                        label: "Address 1",
                        maxLength: 60,
                        required: true,
                        xdQaName: "Address 1"
                    })}
                    {renderInputField({
                        path: "address2",
                        label: "Address 2 / SUITE #: (optional)",
                        maxLength: 60,
                        xdQaName: "Address 2"
                    })}

                    {renderInputField({
                        path: "city",
                        label: "City",
                        maxLength: 60,
                        required: true,
                        xdQaName: "City"
                    })}
                    
                    {form.value && (form.value.countryCode === "US" || form.value.countryCode === "CA") ? (
                        renderField({
                            path: "state",
                            render: ({field, error}) => <>
                                <div className="container-select"> 
                                    {Select({
                                        list: form.value.countryCode === "US" ? US_STATES : CA_PROVINCES,
                                        itemToLabel: (s) => s.name,
                                        onChange: (s) => {
                                            setHasUnsavedChanges(!equalDeep(filterKeys(initValue ?? {}, (key) => key in form.value), setNestedValue(form.value, ['state'], s.abbreviation)))
                                            field.onChange(s.abbreviation)
                                        },
                                        autofill: {
                                            getItem: (str) => (
                                                form.value.countryCode === "US" ? 
                                                (
                                                    US_STATES.find((s) => s.abbreviation === str) ??
                                                    US_STATES.find((s) => s.name.toLowerCase() === str.toLowerCase())
                                                ) : (
                                                    CA_PROVINCES.find((s) => s.abbreviation === str) ??
                                                    CA_PROVINCES.find((s) => s.name.toLowerCase() === str.toLowerCase())
                                                )
                                            ),
                                            name: "state",
                                        },
                                        isSelected: (s) => field.value === s.abbreviation,
                                        xdQaName: "State",
                                        className: field.value ? "valid" : null
                                    })}
                                    <label className="label" >State</label>              
                                    <div className="required">*</div>
                                </div>

                                {error && (
                                    <div className="error-message" xd-qa-type="label" xd-qa-name={"State" + " Error " + error.getMessage()}>
                                        {error.getMessage()}
                                    </div>
                                )}
                            </>
                        })
                    ) : (
                        renderInputField({
                            path: "state",
                            label: "State",
                            maxLength: 20,
                            required: true,
                            xdQaName: "State"
                        })
                    )}
                    {renderInputField({
                        path: "postalCode",
                        label: "Zip",
                        maxLength: 20,
                        required: true,
                        xdQaName: "Zip"
                    })}                    

                    {renderInputField({
                        path: "contactPhone",
                        label: "Phone",
                        maxLength: 20,
                        required: true,
                        xdQaName: "Phone"
                    })}
                </div>
            );
        },
    }),
);

const getCountryCodeFromLabel = (label, countryCodes) => {
    if (countryCodes?.hasOwnProperty(label)) {
        return label;
    }
    const e = entries(countryCodes).find(([key, value]) => {
        if (key === "US" || key === "USA") {
            return (
                label === value ||
                label === "USA" ||
                label === "US" ||
                label === "U.S.A" ||
                label === "United States" ||
                label === "America" ||
                false
            );
        } else {
            return label === value;
        }
    });
    if (e) {
        return e[0];
    }
    return getCountryCodeFromLabel("US", countryCodes);
};

